import { useEffect, useState, useCallback } from 'react';
import {
  AppLayout,
  ConnectivityIndicator,
  Spinner,
  ErrorBanner,
  useOnlineStatus,
} from '@scoring/frontend-shared';
import type { ParticipantScoreView, ParticipantStationScore } from '@scoring/frontend-shared';
import { useApiClient } from '../hooks/useApiClient.js';

const SCORES_CACHE_KEY = 'participant_scores_cache';

/**
 * API response shape for an event the participant is registered in.
 * Matches the `/account/events` response structure.
 */
interface ParticipantEvent {
  eventId: string;
  eventName: string;
  eventDate: string;
  eventStatus: string;
  teamName: string | null;
  eventScopedTeamId: string | null;
  teamPublicId: string | null;
}


/**
 * My Scores page — displays the participant's scores per station with
 * completion status and summary totals.
 *
 * Requirements:
 *   - 15.1: participant can view own scores as they are recorded
 *   - 15.2: show station number, hits, misses, DNS status, running total
 *   - 15.3: show which stations are remaining (not yet scored)
 *   - 18.2: cache scores locally for offline viewing
 *   - 18.3: cache station completion status locally for offline viewing
 *   - 18.4: sync with server on reconnect
 *   - 18.5: display connectivity indicator
 */
export function MyScoresPage(): JSX.Element {
  const api = useApiClient();
  const online = useOnlineStatus();

  const [scores, setScores] = useState<ParticipantScoreView | null>(null);
  const [currentEvent, setCurrentEvent] = useState<ParticipantEvent | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      // Fetch event registrations to find the current event
      const eventsData = await api.request<{ events: ParticipantEvent[] }>('GET', '/account/events');
      const events = eventsData.events;
      const activeEvent = events.find((e) => e.eventStatus === 'active');
      const publishedEvent = events.find((e) => e.eventStatus === 'published');
      const event = activeEvent ?? publishedEvent ?? events[0] ?? null;

      setCurrentEvent(event);

      if (!event) {
        setScores(null);
        setLoading(false);
        return;
      }

      // Fetch the participant's scores for the current event.
      const scoresResponse = await api.request<{ scores: ParticipantScoreView }>(
        'GET',
        `/account/events/${encodeURIComponent(event.eventId)}/scores`,
      );

      setScores(scoresResponse.scores);

      // Cache for offline (Req 18.2)
      try {
        localStorage.setItem(SCORES_CACHE_KEY, JSON.stringify(scoresResponse.scores));
      } catch {
        // localStorage may be unavailable; non-critical
      }
    } catch (err) {
      // Attempt to load from cache for offline display (Req 18.2)
      try {
        const cachedScores = localStorage.getItem(SCORES_CACHE_KEY);
        if (cachedScores) {
          setScores(JSON.parse(cachedScores) as ParticipantScoreView);
        }
      } catch {
        // ignore cache read failures
      }
      setError(err instanceof Error ? err.message : 'Failed to load scores');
    } finally {
      setLoading(false);
    }
  }, [api]);

  // Initial fetch
  useEffect(() => {
    void fetchData();
  }, [fetchData]);

  // Re-sync when coming back online (Req 18.4)
  useEffect(() => {
    if (online) {
      void fetchData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [online]);

  return (
    <AppLayout
      title="My Scores"
      headerRight={<ConnectivityIndicator />}
    >
      {loading && <Spinner label="Loading scores…" />}

      {error && !loading && (
        <ErrorBanner title="Error" onDismiss={() => setError(null)}>
          {error}
        </ErrorBanner>
      )}

      {!loading && !scores && !error && (
        <p style={{ color: 'var(--color-text-secondary, #6B7280)' }}>
          No scores available. You may not be registered for an active event.
        </p>
      )}

      {!loading && scores && (
        <>
          {/* Summary Section */}
          <section aria-label="Score summary" style={{ marginBottom: '1.5rem' }}>
            {currentEvent && (
              <h2 style={{ fontSize: 'var(--font-size-heading-sm, 1.25rem)', margin: '0 0 0.75rem' }}>
                {currentEvent.eventName}
              </h2>
            )}
            <div
              style={{
                display: 'flex',
                gap: '1.5rem',
                padding: '1rem',
                border: '1px solid var(--color-border, #E5E7EB)',
                borderRadius: 'var(--radius-card, 12px)',
                backgroundColor: '#f6f8fa',
              }}
            >
              <div style={{ textAlign: 'center' }}>
                <div style={{ fontSize: '1.5rem', fontWeight: 700, color: 'var(--color-brand-orange, #F48C06)' }}>
                  {scores.totalScore}
                </div>
                <div style={{ fontSize: 'var(--font-size-body-sm, 0.875rem)', color: 'var(--color-text-secondary, #6B7280)' }}>Total Score</div>
              </div>
              <div style={{ textAlign: 'center' }}>
                <div style={{ fontSize: '1.5rem', fontWeight: 700, color: 'var(--color-brand-green, #1B4332)' }}>
                  {scores.stationsCompleted}
                </div>
                <div style={{ fontSize: 'var(--font-size-body-sm, 0.875rem)', color: 'var(--color-text-secondary, #6B7280)' }}>Stations Completed</div>
              </div>
              <div style={{ textAlign: 'center' }}>
                <div style={{ fontSize: '1.5rem', fontWeight: 700, color: 'var(--color-text-secondary, #6B7280)' }}>
                  {scores.stations.length + scores.remainingStations.length}
                </div>
                <div style={{ fontSize: 'var(--font-size-body-sm, 0.875rem)', color: 'var(--color-text-secondary, #6B7280)' }}>Total Stations</div>
              </div>
            </div>
          </section>

          {/* Station Status Grid (Req 15.3) */}
          <section aria-label="Station completion status" style={{ marginBottom: '1.5rem' }}>
            <h3 style={{ fontSize: 'var(--font-size-body-md, 1rem)', margin: '0 0 0.5rem' }}>Station Status</h3>
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fill, minmax(60px, 1fr))',
                gap: '0.5rem',
              }}
            >
              {/* Completion is derived from SCORED stations so the checkmarks
                  always match the "stations completed" count (a station is
                  complete once it has a score, regardless of check-in state). */}
              {[...scores.stations.map((s) => s.stationNumber), ...scores.remainingStations]
                .filter((num, i, arr) => arr.indexOf(num) === i)
                .sort((a, b) => a - b)
                .map((num) => (
                  <StationStatusBadge
                    key={num}
                    stationNumber={num}
                    completed={!scores.remainingStations.includes(num)}
                  />
                ))}
            </div>
          </section>

          {/* Scores Table (Req 15.2) */}
          <section aria-label="Score details">
            <h3 style={{ fontSize: 'var(--font-size-body-md, 1rem)', margin: '0 0 0.5rem' }}>Score Details</h3>
            {scores.stations.length === 0 ? (
              <p style={{ color: 'var(--color-text-secondary, #6B7280)' }}>No scores recorded yet.</p>
            ) : (
              <div style={{ overflowX: 'auto' }}>
                <table
                  style={{
                    width: '100%',
                    borderCollapse: 'collapse',
                    fontSize: 'var(--font-size-body-sm, 0.875rem)',
                  }}
                  aria-label="Scores per station"
                >
                  <thead>
                    <tr
                      style={{
                        borderBottom: '2px solid var(--color-border, #E5E7EB)',
                        textAlign: 'left',
                      }}
                    >
                      <th style={thStyle}>Station</th>
                      <th style={thStyle}>Hits</th>
                      <th style={thStyle}>Misses</th>
                      <th style={thStyle}>DNS</th>
                      <th style={thStyle}>Total</th>
                    </tr>
                  </thead>
                  <tbody>
                    {scores.stations.map((station) => (
                      <ScoreRow key={station.stationNumber} station={station} />
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </section>
        </>
      )}
    </AppLayout>
  );
}

// --- Sub-components ---

interface StationStatusBadgeProps {
  stationNumber: number;
  completed: boolean;
}

function StationStatusBadge({ stationNumber, completed }: StationStatusBadgeProps): JSX.Element {
  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        padding: '0.5rem 0.25rem',
        borderRadius: 'var(--radius-button, 8px)',
        border: `1px solid ${completed ? 'var(--color-brand-green, #1B4332)' : 'var(--color-border, #E5E7EB)'}`,
        backgroundColor: completed ? '#e6f4ea' : 'var(--color-bg-surface, #FFFFFF)',
      }}
      aria-label={`Station ${stationNumber}: ${completed ? 'completed' : 'pending'}`}
    >
      <span
        aria-hidden="true"
        style={{ fontSize: 'var(--font-size-heading-sm, 1.25rem)' }}
      >
        {completed ? '✓' : '○'}
      </span>
      <span style={{ fontSize: 'var(--font-size-body-sm, 0.875rem)', fontWeight: 500, color: '#333' }}>
        {stationNumber}
      </span>
    </div>
  );
}

interface ScoreRowProps {
  station: ParticipantStationScore;
}

function ScoreRow({ station }: ScoreRowProps): JSX.Element {
  return (
    <tr style={{ borderBottom: '1px solid #eaeaea' }}>
      <td style={tdStyle}>{station.stationNumber}</td>
      <td style={tdStyle}>{station.hits}</td>
      <td style={tdStyle}>{station.misses}</td>
      <td style={tdStyle}>
        {station.isDns ? (
          <span
            style={{
              display: 'inline-block',
              padding: '0.125rem 0.4rem',
              borderRadius: 'var(--radius-button, 8px)',
              fontSize: 'var(--font-size-body-sm, 0.875rem)',
              fontWeight: 600,
              backgroundColor: '#fff3cd',
              color: '#856404',
            }}
          >
            DNS
          </span>
        ) : (
          '—'
        )}
      </td>
      <td style={{ ...tdStyle, fontWeight: 600 }}>{station.runningTotal}</td>
    </tr>
  );
}

// --- Styles ---

const thStyle: React.CSSProperties = {
  padding: '0.5rem 0.75rem',
  fontWeight: 600,
  fontSize: 'var(--font-size-body-sm, 0.875rem)',
  color: 'var(--color-text-secondary, #6B7280)',
};

const tdStyle: React.CSSProperties = {
  padding: '0.5rem 0.75rem',
};
